import React from 'react';
import { Box, IconButton, Typography, Divider } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuid } from 'uuid';
import { ProductSelect } from './ProductSelect';
import { VariantSelect } from './VariantSelect';

export interface ProductVariantRow {
  id: string;
  product: any | null;
  variants: any[];
}

interface ProductVariantRowsProps {
  rows: ProductVariantRow[];
  setRows: (rows: ProductVariantRow[]) => void;
}

export function ProductVariantRows({ rows, setRows }: ProductVariantRowsProps) {
  const handleProductChange = (rowId: string, product: any) => {
    setRows(rows.map(r => r.id === rowId ? { ...r, product, variants: [] } : r));
  };

  const handleVariantsChange = (rowId: string, variants: any) => {
    setRows(rows.map(r => r.id === rowId ? { ...r, variants: Array.isArray(variants) ? variants : [] } : r));
  };

  const lastRow = rows[rows.length - 1];
  const canAddRow = lastRow?.product != null && (lastRow?.variants?.length ?? 0) > 0;

  const handleAddRow = () => {
    setRows([...rows, { id: uuid(), product: null, variants: [] }]);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows(rows.filter(r => r.id !== rowId));
  };

  return (
    <Box>
      {rows.map((row, index) => (
        <Box key={row.id}>
          {index > 0 && <Divider sx={{ my: 1.5 }} />}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <ProductSelect
                value={row.product}
                onChange={(product) => handleProductChange(row.id, product)}
              />
              <Box mt={2}>
                <VariantSelect
                  value={row.variants}
                  onChange={(variants) => handleVariantsChange(row.id, variants)}
                  product={row.product}
                  multiple={true}
                />
              </Box>
            </Box>
            <IconButton
              onClick={() => handleRemoveRow(row.id)}
              disabled={rows.length <= 1}
              size='small'
              sx={{ mt: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}

      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center' }}>
        <IconButton onClick={handleAddRow} color='primary' size='small' disabled={!canAddRow}>
          <AddIcon />
        </IconButton>
        <Typography variant='body2' component='span'>
          Add Product
        </Typography>
      </Box>
    </Box>
  );
}
